import React, { useMemo, useContext } from "react";
import clsx from "clsx";

import { Context } from "../../../../../../teacherStore";
import { IActiveLearningIssue } from "../../../../../../state";
import { getStudentAnswersByQuestion } from "../../../../../../libs/ActiveLearning";
import AnswerStats from "../../../../../../atoms/ActiveLearning/AnswerStats";
import StudentAnswer from "../../../../../../molecules/ActiveLearning/StudentAnswer";
import styles from "../../../ActiveLearningIssue.module.scss";
import stylesAnswer from "../../../ActiveLearningAnswer.module.scss";

type Issue1AnswerProps = {
  materialCode: string;
  lessonNo: number;
  issue?: IActiveLearningIssue | undefined;
};

export const Issue1Answer = (props: Issue1AnswerProps) => {
  const { state } = useContext(Context);
  const currentIssueNo = 1;

  const question1 = useMemo(() => {
    return props.issue?.questions.find(question => question.order_num === 1);
  }, [props.issue?.questions]);

  const question2 = useMemo(() => {
    return props.issue?.questions.find(question => question.order_num === 2);
  }, [props.issue?.questions]);

  const answersSortedByStudentName = useMemo(() => {
    return state.active_learning_answers?.sort((a, b) => {
      return a.last_name_kana.localeCompare(b.last_name_kana, "ja");
    });
  }, [state.active_learning_answers]);

  const studentAnswersQ1 = useMemo(() => {
    return answersSortedByStudentName?.map(studentAnswers => {
      const filteredAnswers = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        1,
      );

      return {
        ...studentAnswers,
        answers: filteredAnswers,
      };
    });
  }, [answersSortedByStudentName, props.materialCode, props.lessonNo]);

  const studentAnswerStatsQ1 = useMemo(() => {
    const answered =
      studentAnswersQ1?.filter(
        studentAnswers => studentAnswers.answers.length > 0,
      ).length || 0;
    const total = studentAnswersQ1?.length || 0;
    const unanswered = total - answered;

    return {
      answered,
      total,
      unanswered,
    };
  }, [studentAnswersQ1]);

  const studentAnswersQ2 = useMemo(() => {
    return answersSortedByStudentName?.map(studentAnswers => {
      const filteredAnswers = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        2,
      );

      return {
        ...studentAnswers,
        answers: filteredAnswers,
      };
    });
  }, [answersSortedByStudentName, props.materialCode, props.lessonNo]);

  const studentAnswerStatsQ2 = useMemo(() => {
    const answered =
      studentAnswersQ2?.filter(
        studentAnswers => studentAnswers.answers.length > 0,
      ).length || 0;
    const total = studentAnswersQ2?.length || 0;
    const unanswered = total - answered;

    return {
      answered,
      total,
      unanswered,
    };
  }, [studentAnswersQ2]);

  return (
    <div className={styles.issue}>
      <div className={clsx(styles.question, styles.primary)}>
        <div className={styles.header}>
          <span className={styles.no}>
            WORK{currentIssueNo < 10 ? `0${currentIssueNo}` : currentIssueNo}
          </span>
          <p className={styles.title}>{props.issue?.title}</p>
        </div>
      </div>
      {question1 && (
        <div className={clsx(styles.question, styles.secondary)}>
          <div className={styles.themEntry}>
            <p className={styles.title}>{question1.title}</p>
            <AnswerStats
              answered={studentAnswerStatsQ1.answered}
              total={studentAnswerStatsQ1.total}
              unanswered={studentAnswerStatsQ1.unanswered}
            />
          </div>
          <div className={styles.answersArea}>
            {studentAnswersQ1?.map((studentAnswers, index) => {
              const no = index + 1;
              const displayNo = no < 10 ? `0${no}` : `${no}`;
              const isAnswered = studentAnswers.answers.length > 0;
              return (
                <StudentAnswer
                  key={studentAnswers.student_id}
                  isAnswered={isAnswered}
                  no={displayNo}
                  name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                  classId={studentAnswers.class_id}
                  studentId={studentAnswers.student_id}
                >
                  {isAnswered &&
                    studentAnswers.answers.map(answer => (
                      <p key={answer.id} className={stylesAnswer.answer}>
                        {answer.contents.value}
                      </p>
                    ))}
                </StudentAnswer>
              );
            })}
          </div>
        </div>
      )}
      {question2 && (
        <div className={clsx(styles.question, styles.secondary)}>
          <AnswerStats
            answered={studentAnswerStatsQ2.answered}
            total={studentAnswerStatsQ2.total}
            unanswered={studentAnswerStatsQ2.unanswered}
          />
          <div className={styles.answersArea}>
            {studentAnswersQ2?.map((studentAnswers, index) => {
              const no = index + 1;
              const displayNo = no < 10 ? `0${no}` : `${no}`;
              const isAnswered = studentAnswers.answers.length > 0;
              return (
                <StudentAnswer
                  key={studentAnswers.student_id}
                  isAnswered={isAnswered}
                  no={displayNo}
                  name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                  classId={studentAnswers.class_id}
                  studentId={studentAnswers.student_id}
                >
                  {isAnswered &&
                    studentAnswers.answers.map(answer => (
                      <div
                        key={answer.id}
                        className={stylesAnswer.feedbackTextArea}
                      >
                        <div className={stylesAnswer.block}>
                          <p className={stylesAnswer.title}>想定される行動</p>
                          <p className={stylesAnswer.feedbackAnswer}>
                            {answer.contents.good}
                          </p>
                        </div>
                        <div className={stylesAnswer.block}>
                          <p className={stylesAnswer.title}>
                            もっとこうだったらいのに/こうしたい
                          </p>
                          <p className={stylesAnswer.feedbackAnswer}>
                            {answer.contents.motto}
                          </p>
                        </div>
                      </div>
                    ))}
                </StudentAnswer>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
export default Issue1Answer;
